import * as ScoreAPI from "api/ScoreAPI";
import * as DeployAPI from "api/DeployAPI";

import * as setupReducer from "containers/Setup/reducer";
import * as runReducer from "containers/Run/reducer";
import * as accountReducer from "containers/Accounts/reducer";

const UPDATE_FIELD = "deploy/UPDATE_FIELD";
const UPDATE_SCORE_FIELD = "deploy/UPDATE_SCORE_FIELD";
const UPDATE_SCORES = "deploy/UPDATE_SCORES";
const UPDATE_SCORE = "deploy/UPDATE_SCORE";
const RESET_SCORES = "deploy/RESET_SCORES";

const initialState = {};

function deployReducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_SCORES:
      return {
        ...state,
        ...action.data
      };
    case UPDATE_FIELD:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          [action.key]: action.data
        }
      };
    case UPDATE_SCORE:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ["scores"]: state[action.id]
            ? {
                ...state[action.id].scores,
                [action.scoreId]: action.data
              }
            : {
                [action.scoreId]: action.data
              }
        }
      };
    case UPDATE_SCORE_FIELD:
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          ["scores"]: {
            ...state[action.id].scores,
            [action.scoreId]: {
              ...state[action.id].scores[action.scoreId],
              [action.key]: action.data
            }
          }
        }
      };
    case RESET_SCORES:
      return initialState;
    default:
      return state;
  }
}

/* Selectors */
export function getDeploy(state) {
  return state.deploy;
}

export function getDeployRun(state, jobId, modelId) {
  return getDeploy(state)[getId(jobId, modelId)] ? getDeploy(state)[getId(jobId, modelId)] : {};
}

export function getScores(state, jobId, modelId) {
  return getDeploy(state)[getId(jobId, modelId)] ? getDeploy(state)[getId(jobId, modelId)].scores : undefined;
}

export function getScore(state, jobId, modelId, scoreId) {
  return getScores(state, jobId, modelId) ? getScores(state, jobId, modelId)[scoreId] : undefined;
}

export function getScoresArr(state) {
  const deploy = getDeploy(state);

  return deploy ? Object.keys(deploy).map((key) => deploy[key]) : [];
}

export function getSortedScores(state, jobId, modelId) {
  const scores = getScores(state, jobId, modelId);

  return scores
    ? Object.keys(scores)
        .map((sKey) => scores[sKey])
        .sort((a, b) => new Date(b.created_date).getTime() - new Date(a.created_date).getTime())
    : [];
}

export function getCsv(state, jobId, modelId) {
  return getDeployRun(state, jobId, modelId).CSV;
}

export function getError(state, jobId, modelId) {
  return getDeployRun(state, jobId, modelId).error;
}

export function getIsDownloadLoading(state, jobId, modelId) {
  return getDeployRun(state, jobId, modelId).isDownloadLoading;
}

export function getProcessingScores(state) {
  const scores = [];

  for (const jobModel of getScoresArr(state)) {
    if (jobModel.scores !== undefined) {
      for (const sKey of Object.keys(jobModel.scores)) {
        const score = jobModel.scores[sKey];

        if (score.status !== "DONE" && score.status !== "ERROR") {
          scores.push(score);
        }
      }
    }
  }

  return scores;
}

export function getId(jobId, modelId) {
  return jobId + "@_@" + modelId;
}

/* Actions */
export function fetchScores() {
  return (dispatch, getState) => {
    return ScoreAPI.getScores().then((scores) => {
      let scoreState = {};

      for (const score of scores) {
        const id = getId(score.job_id, score.model_name);

        if (scoreState[id] !== undefined) {
          scoreState[id].scores[score.id] = {
            ...score,
            errors: score.annotations.filter((annotation) => annotation.type === "error"),
            warnings: score.annotations.filter((annotation) => annotation.type === "warning")
          };
        } else {
          scoreState[id] = {
            scores: {
              [score.id]: {
                ...score,
                errors: score.annotations.filter((annotation) => annotation.type === "error"),
                warnings: score.annotations.filter((annotation) => annotation.type === "warning")
              }
            }
          };
        }
      }

      return dispatch(updateScores(scoreState));
    });
  };
}

export function updateScore(jobId, modelId, score) {
  return {
    type: UPDATE_SCORE,
    id: getId(jobId, modelId),
    scoreId: score.id,
    data: score
  };
}

export function updateScores(scores) {
  return { type: UPDATE_SCORES, data: scores };
}

export function resetScores() {
  return { type: RESET_SCORES };
}

export function updateCSV(jobId, modelId, CSVData) {
  return {
    type: UPDATE_FIELD,
    id: getId(jobId, modelId),
    data: CSVData,
    key: "CSV"
  };
}

export function updateError(jobId, modelId, error) {
  return {
    type: UPDATE_FIELD,
    id: getId(jobId, modelId),
    data: error,
    key: "error"
  };
}

export function updateDownloadLoading(jobId, modelId, scoreId, isDownloadLoading) {
  return {
    type: UPDATE_SCORE_FIELD,
    id: getId(jobId, modelId),
    scoreId: scoreId,
    data: isDownloadLoading,
    key: "isDownloadLoading"
  };
}

export function pollStatus(scoreId) {
  return (dispatch, getState) => {
    return ScoreAPI.getScoreStatus(scoreId).then((response) => {
      const score = getScore(getState(), response.job_id, response.model_name, scoreId);

      dispatch(
        updateScore(response.job_id, response.model_name, {
          ...response,
          errors: response.annotations.filter((annotation) => annotation.type === "error"),
          fileName: response ? response.fileName : "scored_dataset.csv"
        })
      );

      if (response && response.status && response.status !== "DONE" && response.status !== "ERROR") {
        setTimeout(() => dispatch(pollStatus(scoreId)), 5000);
      }
    });
  };
}

export function pollStatusOnce(scoreId) {
  return (dispatch, getState) => {
    return ScoreAPI.getScoreStatus(scoreId).then((response) => {
      const score = getScore(getState(), response.job_id, response.model_name, scoreId);

      dispatch(
        updateScore(response.job_id, response.model_name, {
          ...response,
          errors: response.annotations.filter((annotation) => annotation.type === "error"),
          fileName: response ? response.fileName : "scored_dataset.csv"
        })
      );
    });
  };
}

export function pollScores() {
  return (dispatch, getState) => {
    const state = getState();

    const scores = getProcessingScores(state);
    const selectedAccount = accountReducer.getSelectedAccount(state);

    for (const score of scores) {
      if (selectedAccount && selectedAccount.id === score.account_id && selectedAccount.tenant_id === score.tenant_id) {
        dispatch(pollStatusOnce(score.id));
      }
    }
  };
}

export function handleDownload(jobId, scoreId, modelId, url) {
  return (dispatch, getState) => {
    dispatch(updateDownloadLoading(jobId, modelId, scoreId, true));

    return ScoreAPI.getScoreFile(scoreId)
      .then((response) => {
        dispatch(updateDownloadLoading(jobId, modelId, scoreId, false));

        return response.data.then((csv) => downloadFile(csv, response.fileName, "text/csv"));
      })
      .catch((error) => {
        dispatch(updateDownloadLoading(jobId, modelId, scoreId, false));
        return window.open(url);
      });
  };
}

export function testPredict(jobId, modelId) {
  return (dispatch, getState) => {
    const state = getState();

    const dataset = setupReducer.getDatasetFromSetup(state, jobId);

    const rows = [
      Object.keys(dataset.fields)
        .map((f) => f)
        .join(",") + "\n",
      Object.keys(dataset.fields)
        .map((f) => dataset.fields[f])
        .map((f) => (f.type === "categorical" ? f.data[0].value : f.min))
        .join(",") + "\n"
    ];

    const file = new File(rows, "Helpme.csv", {
      type: "text/csv"
    });

    return ScoreAPI.uploadPrediction(jobId, "aible-relative-impact", file).then((response) => {
      dispatch(handleGetPrediction(jobId, "aible-relative-impact", response.id));
    });
  };
}

function handleGetPrediction(jobId, modelId, scoreId) {
  return (dispatch, getState) => {
    const state = getState();

    const score = getScore(state, jobId, modelId, scoreId);

    if (score === undefined || (score.status !== "DONE" && score.status !== "ERROR")) {
      dispatch(pollStatusOnce(scoreId));
      setTimeout(() => dispatch(handleGetPrediction(jobId, modelId, scoreId)), 5000);
    } else {
      if (score.status === "DONE") {
        ScoreAPI.getScoreFile(scoreId).then((response) => {
          response.data.then((csv) => {
            console.log(csv);
          });
        });
      }
    }
  };
}

function downloadFile(content, fileName, mimeType) {
  var a = document.createElement("a");
  mimeType = mimeType || "application/octet-stream";

  if (navigator.msSaveBlob) {
    // IE10
    navigator.msSaveBlob(
      new Blob([content], {
        type: mimeType
      }),
      fileName
    );
  } else if (URL && "download" in a) {
    //html5 A[download]
    a.href = URL.createObjectURL(
      new Blob([content], {
        type: mimeType
      })
    );
    a.setAttribute("download", fileName);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}

export function handleUpload(jobId, modelId, response) {
  return (dispatch, getState) => {
    dispatch(updateError(jobId, modelId, undefined));

    if (response && response.id) {
      dispatch(updateScore(jobId, modelId, response));

      return dispatch(pollStatus(response.id));
    }

    if (response && response.success === false && response.message) {
      dispatch(updateError(jobId, modelId, response.message));
    }
  };
}

export function handleFileChange(jobId, modelId, file) {
  return (dispatch, getState) => {
    dispatch(runReducer.updateIsScoreUploading(jobId, true));

    const accountId = runReducer.getJobAccountId(getState(), jobId);

    return ScoreAPI.uploadPrediction(jobId, modelId, file, accountId)
      .then((response) => {
        dispatch(runReducer.updateIsScoreUploading(jobId, false));
        return dispatch(updateScore(jobId, modelId, response));
      })
      .catch((e) => {
        dispatch(runReducer.updateIsScoreUploading(jobId, false));
      });
  };
}

export default deployReducer;
